import PanelCard, { PANEL_TABLE_HEAD_CLASS, PANEL_TABLE_ROW_CLASS, SECTION_TITLE_CLASS } from 'components/card/PanelCard';
import { SkeletonLine } from 'components/skeleton/Skeleton';
import { amountFromPercentage, sortRanges } from 'helper/coefficientRange';
import useText from 'hooks/useText';
import { useEffect, useState } from 'react';
import ApiDataBase from 'server';

const perHour = (amount) => `${amount.toFixed(2).replace('.', ',')} €/h`;
const coefficient = (value) =>
  value === null || value === undefined
    ? '—'
    : Number(value).toFixed(2).replace('.', ',');
const ROW = 'grid grid-cols-[2fr_1fr_1fr] items-center gap-2 px-4';

/**
 * « Coefficients de facturation », as on the prototype: each range of the
 * gross hourly rate (from the minimum wage) with its delegation and
 * management coefficients, then who attested them. `onEdit` opens the grid.
 */
const CoefficientsCard = ({
  coefficients,
  attestation,
  isDefault,
  badge,
  onEdit,
  cardRef,
  className = '',
}) => {
  const { text } = useText('remuneration');
  const [minimumWage, setMinimumWage] = useState(null);
  useEffect(() => {
    ApiDataBase.getMinimumWage(
      (data) =>
        data?.hourly?.gross && setMinimumWage(parseFloat(data.hourly.gross)),
      () => {},
    );
  }, []);

  const ranges = sortRanges(coefficients ?? []);
  const amountAt = (percentage) =>
    amountFromPercentage(minimumWage, percentage);
  const rangeLabel = (range, index) => {
    if (!minimumWage) return null;
    const from =
      index === 0 ? minimumWage : amountAt(ranges[index - 1].percentage);
    if (from === null) return null;
    // A range starts where the one before ends: past the first, its start says enough.
    if (index > 0) return text('rangeOver', { from: perHour(from) });
    const to = amountAt(range.percentage);
    return to === null
      ? perHour(from)
      : text('rangeFromTo', { from: perHour(from), to: perHour(to) });
  };
  const attestedBy = attestation?.by
    ? `${attestation.by.firstname ?? ''} ${(attestation.by.lastname ?? '').toUpperCase()}`.trim()
    : '';
  const attestedOn = attestation?.at
    ? new Date(attestation.at).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <div ref={cardRef} className={`rounded-xl ${className}`}>
      <PanelCard
        title={text('coefficientsTitle')}
        titleClassName={SECTION_TITLE_CLASS}
        collapsible
        badge={badge}
        onEdit={onEdit}
        footer={
          <p className="px-1 text-xs text-gray-500">
            {attestedOn && !isDefault
              ? attestedBy
                ? text('attestedBy', { day: attestedOn, name: attestedBy })
                : text('attested', { day: attestedOn })
              : text('neverAttested')}
          </p>
        }
      >
        <div
          className={`${ROW} ${PANEL_TABLE_HEAD_CLASS}`}
        >
          <span>{text('columnRate')}</span>
          <span className="text-right">{text('columnDelegation')}</span>
          <span className="text-right">{text('columnManagement')}</span>
        </div>
        {ranges.map((range, index) => (
          <div
            key={`${range.percentage}-${index}`}
            className={`${ROW} ${PANEL_TABLE_ROW_CLASS}`}
          >
            <span className="flex min-w-0 items-center gap-2 text-gray-900">
              {/* The ranges read from the minimum wage: a line holds their
                  place until it is known. */}
              {minimumWage ? rangeLabel(range, index) : <SkeletonLine className="w-28" />}
              {Number.isFinite(range.percentage) && (
                <span className="rounded border border-gray-200 px-1.5 py-0.5 text-[11px] font-medium text-gray-700">
                  +{range.percentage.toFixed(2).replace('.', ',')} %
                </span>
              )}
            </span>
            <span className="text-right text-gray-900">
              {coefficient(range.delegation)}
            </span>
            <span className="text-right text-gray-900">
              {coefficient(range.management)}
            </span>
          </div>
        ))}
      </PanelCard>
    </div>
  );
};

export default CoefficientsCard;
